import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ShieldCheck,
  Cpu,
  Layers,
  FileCheck,
  Microscope,
  Ribbon,
  ChevronRight,
} from "lucide-react";
import AbstractShapes from "@/components/landing/AbstractShapes";

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Medical-only guarantee",
    range: "0 GENERIC",
    desc: "Every ImageNet / COCO / ADE20K / general-SAM code path was physically removed. Public HMAC-SHA256 signed IRB manifest verifies it at request time.",
    bullets: [
      "Public compliance manifest",
      "Runtime scanner enforces the guarantee",
      "Non-medical modalities return HTTP 422",
    ],
  },
  {
    icon: Cpu,
    title: "PACS-native DICOMweb ingest",
    range: "WADO-RS",
    desc: "Pull a full study by StudyInstanceUID from a hospital PACS via QIDO-RS + WADO-RS. PHI is stripped on the way in — no PHI ever touches disk.",
    bullets: [
      "PatientName / MRN / DOB anonymised on ingest",
      "Skip the manual upload entirely",
      "Append-only audit log per prediction",
    ],
  },
  {
    icon: Layers,
    title: "RadPath correlation viewer",
    range: "RAD ↔ PATH",
    desc: "Draw a lesion on the mammogram and the matching tile on the whole-slide image. Mapping stored in normalised coordinates, exported to the research PDF.",
    bullets: [
      "Real OpenSlide pyramid thumbnails",
      "3D Slicer / Fiji / QuPath launchers",
      "24-hour public share link",
    ],
  },
];

const STATS = [
  { v: "20", l: "Curated models & notebooks" },
  { v: "06", l: "Modalities (FFDM · US · DBT · MRI · CXR · WSI)" },
  { v: "RUO", l: "Research Use Only · IRB-verifiable" },
];

const MODALITIES = [
  { code: "FFDM", label: "Full-field mammography", detail: "MammoScreen · YOLO11m KETEM · LIBRA" },
  { code: "US", label: "Breast ultrasound", detail: "BUSI-ViT · SegFormer-B0 · MedSAM-2" },
  { code: "DBT", label: "Digital breast tomosynthesis", detail: "Duke DBT Faster R-CNN · 3D tracker" },
  { code: "MRI", label: "Dynamic contrast MRI", detail: "MAMA-MIA nnU-Net · FGT % density" },
  { code: "CXR", label: "Chest X-ray (18 findings)", detail: "TorchXRayVision · BiomedCLIP" },
  { code: "WSI", label: "Digital pathology (H&E · IHC)", detail: "HoverNet · CAMELYON17 · Ibex ref." },
];

export default function BreastHubProject() {
  return (
    <section
      id="breasthub"
      data-testid="breasthub-section"
      className="relative py-24 md:py-32 bg-white border-y border-[var(--sci-border)] overflow-hidden"
    >
      {/* Subtle blush accent — breast-health awareness context, kept academic */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -top-40 w-[520px] h-[520px] rounded-full opacity-60"
        style={{
          background:
            "radial-gradient(closest-side, rgba(232, 138, 158, 0.15), transparent)",
        }}
      />
      <AbstractShapes
        variant="rings"
        tone="ink"
        className="-left-24 bottom-0 w-[420px] h-[420px] opacity-50"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14 md:mb-20 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="overline text-[var(--sci-teal)]">
                · 08 / Initiative · 2026
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-sci tracking-[0.2em] uppercase border border-[var(--sci-border)] px-2 py-1 text-[var(--sci-text-muted)]">
                <Ribbon size={11} className="text-[#c86a83]" />
                Breast Health Intelligence
              </span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02]">
              BreastHub<span className="text-[var(--sci-teal)]">.</span> <br />
              The research console for{" "}
              <em className="not-italic text-[var(--sci-teal)]">
                AI-assisted breast imaging.
              </em>
            </h2>
          </div>
          <p className="lg:col-span-5 text-[var(--sci-text-muted)] leading-relaxed text-base md:text-lg">
            Built for the radiologists, PhDs and multi-centre consortia who
            actually stare at breast scans. 20 curated models across
            mammography, ultrasound, DBT, MRI and WSI pathology — pre-wired,
            provenance-tagged, reproducibility-contracted.
          </p>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--sci-border)] border border-[var(--sci-border)] mb-14 md:mb-16">
          {STATS.map((s, i) => (
            <div
              key={s.l}
              data-testid={`breasthub-stat-${i}`}
              className="bg-white p-6 md:p-8"
            >
              <div className="font-serif-display text-4xl md:text-5xl text-[var(--sci-navy)] leading-none">
                {s.v}
              </div>
              <div className="text-xs md:text-sm text-[var(--sci-text-muted)] mt-3 leading-snug">
                {s.l}
              </div>
            </div>
          ))}
        </div>

        {/* Feature trio */}
        <div className="grid md:grid-cols-3 gap-px bg-[var(--sci-border)] border border-[var(--sci-border)] mb-14 md:mb-16">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5, delay: 0.06 * i }}
                data-testid={`breasthub-feature-${i}`}
                className="bg-white p-8 md:p-10 group hover:bg-[var(--sci-bg)] transition-colors"
              >
                <div className="flex items-start justify-between mb-7">
                  <div className="w-11 h-11 border border-[var(--sci-border)] flex items-center justify-center text-[var(--sci-teal)] group-hover:border-[var(--sci-teal)] transition-colors">
                    <Icon size={18} strokeWidth={1.5} />
                  </div>
                  <div className="font-mono-sci text-[10px] tracking-[0.22em] text-[var(--sci-teal)]/80">
                    {f.range}
                  </div>
                </div>
                <h3 className="font-serif-display text-2xl md:text-[28px] leading-tight tracking-tight text-[var(--sci-navy)]">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm text-[var(--sci-text-muted)] leading-relaxed">
                  {f.desc}
                </p>
                <ul className="mt-6 space-y-2">
                  {f.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-xs text-[var(--sci-text)]/80"
                    >
                      <ChevronRight
                        size={12}
                        className="text-[var(--sci-teal)] mt-0.5 shrink-0"
                      />
                      <span className="leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Modality matrix */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-14 md:mb-16">
          <div className="lg:col-span-4">
            <div className="overline text-[var(--sci-teal)] mb-4">
              · Modality coverage
            </div>
            <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] tracking-tight leading-tight">
              Six modalities. <br />
              <em className="not-italic text-[var(--sci-teal)]">
                One provenance chain.
              </em>
            </h3>
            <p className="mt-4 text-sm text-[var(--sci-text-muted)] leading-relaxed">
              Every prediction is versioned so a peer reviewer can always trace
              which model produced which BI-RADS.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-[var(--sci-border)] border border-[var(--sci-border)]">
            {MODALITIES.map((m, i) => (
              <div
                key={m.code}
                data-testid={`breasthub-modality-${i}`}
                className="bg-white p-5 md:p-6 hover:bg-[var(--sci-bg)] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="font-mono-sci text-[10px] tracking-[0.22em] text-[var(--sci-teal)] border border-[var(--sci-border)] px-2 py-1">
                    {m.code}
                  </div>
                  <div className="font-serif-display text-base md:text-lg text-[var(--sci-navy)] leading-tight">
                    {m.label}
                  </div>
                </div>
                <div className="text-[11px] text-[var(--sci-text-muted)] mt-2.5 leading-snug font-mono-sci tracking-wider">
                  {m.detail}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer strip: RUO banner + CTAs */}
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end pt-10 border-t border-[var(--sci-border)]">
          <div className="md:col-span-7 flex items-start gap-3">
            <FileCheck
              size={16}
              className="text-[var(--sci-teal)] shrink-0 mt-0.5"
            />
            <p className="text-xs text-[var(--sci-text-muted)] leading-relaxed max-w-2xl">
              <span className="font-mono-sci tracking-wider text-[var(--sci-navy)]">
                RESEARCH USE ONLY.
              </span>{" "}
              Not intended for clinical diagnosis, treatment decisions, or
              patient management. All outputs require review by qualified
              healthcare professionals. PHI is stripped on ingest and every
              prediction is written to an append-only audit log.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-wrap gap-3 justify-start md:justify-end">
            <a
              href="https://breasthub.net/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="breasthub-visit"
              className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm tracking-wide"
            >
              Visit BreastHub
              <ArrowUpRight size={14} />
            </a>
            <a
              href="https://breasthub.net/models"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="breasthub-models"
              className="btn-secondary inline-flex items-center gap-2 px-6 py-3 text-sm tracking-wide"
            >
              <Microscope size={14} />
              Browse 20 Model Cards
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
